/**
 * Uptime Status
 *
 * Sentry does the probing; this groups the results by brand, which is a concept
 * that only exists in core-management. A flat list of 32 monitors in Sentry
 * cannot answer "is MDRX healthy" at a glance — that is what this is for.
 *
 * Surfaces we deliberately do not monitor are shown explicitly rather than
 * omitted, so a coverage gap never reads as health.
 */

import { useState, useEffect } from 'react'
import { AlertCircle, CheckCircle2, XCircle, HelpCircle, ShieldOff, Loader2, ExternalLink } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card'
import { statusAPI } from '../services/api'

const STATUS_STYLE = {
  ok:       { icon: CheckCircle2, cls: 'text-emerald-600', label: 'Up' },
  down:     { icon: XCircle,      cls: 'text-red-600',     label: 'Down' },
  missing:  { icon: AlertCircle,  cls: 'text-amber-600',   label: 'No monitor' },
  unknown:  { icon: HelpCircle,   cls: 'text-muted-foreground', label: 'Unknown' },
  blocked:  { icon: ShieldOff,    cls: 'text-muted-foreground', label: 'Not monitored' },
  disabled: { icon: ShieldOff,    cls: 'text-muted-foreground', label: 'Disabled' },
}

function StatusIcon({ status }) {
  const s = STATUS_STYLE[status] || STATUS_STYLE.unknown
  const Icon = s.icon
  return <Icon className={`w-4 h-4 shrink-0 ${s.cls}`} aria-label={s.label} />
}

function formatInterval(seconds) {
  if (!seconds) return '—'
  return seconds < 60 ? `${seconds}s` : `${Math.round(seconds / 60)}m`
}

export default function StatusPage() {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      try {
        const res = await statusAPI.getUptime()
        if (!cancelled && res.data?.success) setData(res.data.data)
      } catch (err) {
        if (!cancelled) setError(err.response?.data?.error?.message || err.message)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    // Slowest check interval is 5 minutes, so refreshing faster shows nothing new.
    const timer = setInterval(load, 60_000)
    return () => { cancelled = true; clearInterval(timer) }
  }, [])

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-muted-foreground">
        <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading status…
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="flex items-start gap-2 p-3 rounded-lg bg-red-50 text-red-700 text-sm">
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>Could not load uptime status: {error}</span>
        </div>
      </div>
    )
  }

  const { brands = [], summary = {}, context = {} } = data || {}

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-foreground">Uptime</h1>
        <p className="text-sm text-muted-foreground mt-1">
          {summary.monitors_enabled} monitors across {summary.brands} brands, checked by Sentry.
          Intake is checked every minute; everything else every five.
        </p>
      </div>

      {!context.live_status_available && (
        <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 text-amber-700 text-sm">
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>
            {context.error} Statuses below show the intended monitor set, not live results.
          </span>
        </div>
      )}

      {summary.surfaces_blocked > 0 && (
        <div className="flex items-start gap-2 p-3 rounded-lg bg-muted text-muted-foreground text-sm">
          <ShieldOff className="w-4 h-4 mt-0.5 shrink-0" />
          <span>
            {summary.surfaces_blocked} surface{summary.surfaces_blocked === 1 ? '' : 's'} cannot be
            monitored and {summary.surfaces_blocked === 1 ? 'is' : 'are'} listed below as
            “Not monitored”. These are gaps in coverage, not outages.
          </span>
        </div>
      )}

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        {brands.map(brand => (
          <Card key={brand.brand}>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-base capitalize">
                <StatusIcon status={brand.status} />
                {brand.brand}
              </CardTitle>
              <CardDescription>
                {brand.monitored_count} monitored
                {brand.blocked_count > 0 && ` · ${brand.blocked_count} not monitored`}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              {brand.surfaces.map(s => (
                <div key={s.url} className="text-sm">
                  <div className="flex items-center gap-2">
                    <StatusIcon status={s.status} />
                    <span className="capitalize font-medium">{s.label}</span>
                    <span className="text-xs text-muted-foreground ml-auto">
                      {['blocked', 'disabled'].includes(s.status)
                        ? STATUS_STYLE[s.status].label
                        : formatInterval(s.interval_seconds)}
                    </span>
                    {s.monitor_url && (
                      <a href={s.monitor_url} target="_blank" rel="noreferrer"
                         className="text-muted-foreground hover:text-foreground">
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                  </div>
                  <div className="pl-6 text-xs text-muted-foreground break-all">{s.url}</div>
                  {s.blocked_reason && (
                    <div className="pl-6 text-xs text-amber-700 mt-1">{s.blocked_reason}</div>
                  )}
                  {s.note && (
                    <div className="pl-6 text-xs text-muted-foreground italic mt-1">{s.note}</div>
                  )}
                </div>
              ))}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  )
}
